import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { sanitizeRichText } from '../utils/richText';
import GameStats from './GameStats';

/**
 * Full game description, formatted as written in the admin editor
 * (headings, bold, lists, links), in a native modal <dialog> — which brings
 * focus handling, Esc-to-close and the right semantics for screen readers.
 */
export default function GameInfoDialog({ game, open, onClose }) {
  const ref = useRef(null);
  const slug = game.slug || game.id;
  const html = useMemo(() => sanitizeRichText(game.description), [game.description]);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      document.documentElement.classList.add('dialog-open'); // no page scroll behind it
    } else if (!open && dlg.open) {
      dlg.close();
    }
    return () => document.documentElement.classList.remove('dialog-open');
  }, [open]);

  const titleId = `game-info-title-${slug}`;

  return (
    <dialog
      ref={ref}
      className="game-info-dialog"
      aria-labelledby={titleId}
      onClose={onClose}
      // a click on the backdrop lands on the <dialog> element itself
      onClick={(e) => { if (e.target === ref.current) onClose(); }}
    >
      <div className="game-info-panel">
        <header className="game-info-header">
          <div>
            {game.category && <div className="game-card-category">{game.category}</div>}
            <h2 id={titleId} className="game-info-title">{game.name}</h2>
          </div>
          <button type="button" className="game-info-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>

        <div className="game-info-body">
          {html ? (
            // sanitised through an allowlist in utils/richText.js
            <div className="rich-text" dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <p className="game-info-empty">No description yet.</p>
          )}
        </div>

        <footer className="game-info-footer">
          <GameStats game={game} />
          <Link className="btn-demo" to={`/game/play/${slug}`} onClick={onClose}>
            Play Demo <span className="btn-demo-arrow">→</span>
          </Link>
        </footer>
      </div>
    </dialog>
  );
}
